import type { EntradasDoAno, EntradasDoMes, FunilProjetado } from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  EstadoCarregando,
  EstadoDeErro,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  toast,
} from "@base/ui";
import { CalendarPlus, LoaderCircle, Target, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { parametroDaUrl } from "../../../shared/navegacao/navegacao";
import {
  useAnosDoFunil,
  useCriarAno,
  useRealizadoDoAno,
  useRemoverAno,
  useSalvarAno,
} from "../application/funil-projetado.contexto";
import { calcularFunil } from "../domain/calculo";
import { lerEntrada, formatarEntrada } from "../domain/entrada";
import { atingido, somarRealizado } from "../domain/linhas";
import { formatarAtingido, formatarValor } from "./formatos";
import { GradeDoFunil } from "./grade-do-funil";

export const CAMINHO_DO_FUNIL_PROJETADO = "/configuracoes/funil-projetado";

const entradasDe = (funil: FunilProjetado): EntradasDoAno => ({
  escalaDeVerba: funil.escalaDeVerba,
  meses: funil.meses.map((mes) => ({ ...mes })),
});

/**
 * Funil Projetado do cliente: um funil por ano. As celulas escuras sao
 * digitadas; o resto (leads, vendas, faturamento, custos) e calculado na hora,
 * e o realizado do CRM aparece logo abaixo de cada meta.
 */
export function PaginaDeFunilProjetado({ clienteId, cliente }: { readonly clienteId: string; readonly cliente: string }) {
  const anos = useAnosDoFunil(clienteId);
  const [anoEscolhido, setAnoEscolhido] = useState<number | null>(() => {
    const daUrl = Number(parametroDaUrl("ano"));
    return Number.isInteger(daUrl) && daUrl > 0 ? daUrl : null;
  });

  const lista = anos.data ?? [];
  const funil = lista.find((item) => item.ano === anoEscolhido) ?? lista[0] ?? null;

  function escolher(ano: number) {
    setAnoEscolhido(ano);
    window.history.replaceState(null, "", `${CAMINHO_DO_FUNIL_PROJETADO}?ano=${ano}`);
  }

  const cabecalho = (
    <CabecalhoDePagina
      rotulo={`${cliente} · Configurações`}
      titulo="Funil Projetado"
      descricao="As metas do ano, mês a mês. Você define orçamento, custo por lead, taxas e ticket; o funil e o faturamento são calculados."
    />
  );

  if (anos.isPending) {
    return (
      <div className="flex flex-col gap-secao">
        {cabecalho}
        <EstadoCarregando rotulo="Carregando o funil projetado" />
      </div>
    );
  }
  if (anos.isError) {
    return (
      <div className="flex flex-col gap-secao">
        {cabecalho}
        <EstadoDeErro mensagem={mensagemDoErro(anos.error)} aoTentarNovamente={() => void anos.refetch()} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-secao">
      {cabecalho}

      <div className="-mt-4 flex flex-wrap items-center gap-2">
        <nav aria-label="Anos do funil projetado" className="flex flex-wrap gap-2">
          {lista.map((item) => (
            <button
              key={item.ano}
              type="button"
              aria-current={funil?.ano === item.ano ? "page" : undefined}
              onClick={() => escolher(item.ano)}
              className={cn(
                "h-10 rounded-full px-5 text-sm font-semibold tabular-nums transition-colors",
                funil?.ano === item.ano
                  ? "bg-secondary text-secondary-foreground shadow-sm"
                  : "bg-card text-muted-foreground ring-1 ring-border hover:text-foreground",
              )}
            >
              {item.ano}
            </button>
          ))}
        </nav>
        <AdicionarAno clienteId={clienteId} existentes={lista.map((item) => item.ano)} aoCriar={escolher} />
      </div>

      {funil ? (
        <EditorDoAno key={`${funil.ano}:${funil.atualizadoEm}`} clienteId={clienteId} funil={funil} />
      ) : (
        <div className="flex flex-col items-center gap-4 rounded-3xl bg-card px-6 py-16 text-center shadow-sm ring-1 ring-border/70">
          <span className="grid size-14 place-items-center rounded-2xl bg-secondary text-ice">
            <Target className="size-6" aria-hidden="true" />
          </span>
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-medium">Nenhum ano planejado ainda</h2>
            <p className="max-w-md text-sm text-muted-foreground">
              Adicione o ano para definir as metas de cada mês. Depois é só acompanhar o realizado logo abaixo de cada
              meta.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function AdicionarAno({
  clienteId,
  existentes,
  aoCriar,
}: {
  readonly clienteId: string;
  readonly existentes: readonly number[];
  readonly aoCriar: (ano: number) => void;
}) {
  const sugerido = existentes.length > 0 ? Math.max(...existentes) + 1 : new Date().getFullYear();
  const [aberto, setAberto] = useState(false);
  const [ano, setAno] = useState(String(sugerido));
  const [copiarDe, setCopiarDe] = useState<string>("");
  const criar = useCriarAno(clienteId);
  const numero = Number(ano);
  const valido = Number.isInteger(numero) && numero >= 2020 && numero <= 2100 && !existentes.includes(numero);

  return (
    <Popover
      open={aberto}
      onOpenChange={(proximo) => {
        setAberto(proximo);
        if (proximo) {
          setAno(String(sugerido));
          setCopiarDe(existentes.length > 0 ? String(Math.max(...existentes)) : "");
          criar.reset();
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-10 rounded-full">
          <CalendarPlus aria-hidden="true" />
          Adicionar ano
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-72 flex-col gap-4 p-5">
        <form
          className="flex flex-col gap-4"
          onSubmit={(evento) => {
            evento.preventDefault();
            if (!valido) return;
            criar.mutate(
              { ano: numero, copiarDe: copiarDe ? Number(copiarDe) : undefined },
              {
                onSuccess: () => {
                  setAberto(false);
                  aoCriar(numero);
                },
              },
            );
          }}
        >
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Ano
            <input
              value={ano}
              onChange={(evento) => setAno(evento.target.value.replace(/\D/g, "").slice(0, 4))}
              inputMode="numeric"
              aria-invalid={!valido}
              className="h-10 rounded-lg border border-input bg-card px-3 text-sm tabular-nums outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/25 aria-invalid:border-destructive"
            />
            {existentes.includes(numero) ? (
              <span className="text-xs font-normal text-destructive">Esse ano já existe.</span>
            ) : null}
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            Começar com
            <select
              value={copiarDe}
              onChange={(evento) => setCopiarDe(evento.target.value)}
              className="h-10 rounded-lg border border-input bg-card px-3 text-sm outline-none focus-visible:border-primary"
            >
              <option value="">Metas em branco</option>
              {existentes.map((item) => (
                <option key={item} value={item}>
                  As metas de {item}
                </option>
              ))}
            </select>
          </label>
          {criar.isError ? <p className="text-sm text-destructive">{mensagemDoErro(criar.error)}</p> : null}
          <Button type="submit" disabled={!valido || criar.isPending}>
            {criar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
            Criar funil de {valido ? numero : "…"}
          </Button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

function EditorDoAno({ clienteId, funil }: { readonly clienteId: string; readonly funil: FunilProjetado }) {
  const [entradas, setEntradas] = useState<EntradasDoAno>(() => entradasDe(funil));
  const salvar = useSalvarAno(clienteId);
  const remover = useRemoverAno(clienteId);
  const realizado = useRealizadoDoAno(clienteId, funil.ano);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const calculado = useMemo(() => calcularFunil(entradas), [entradas]);
  const alterado = JSON.stringify(entradas) !== JSON.stringify(entradasDe(funil));
  const hoje = new Date();
  const mesAtual = hoje.getFullYear() === funil.ano ? hoje.getMonth() + 1 : null;

  // Sair da pagina com alteracao nao salva pergunta antes.
  useEffect(() => {
    if (!alterado) return;
    const avisar = (evento: BeforeUnloadEvent) => evento.preventDefault();
    window.addEventListener("beforeunload", avisar);
    return () => window.removeEventListener("beforeunload", avisar);
  }, [alterado]);

  function mudarMes(indice: number, campo: keyof EntradasDoMes, valor: number) {
    setEntradas((atual) => ({
      ...atual,
      meses: atual.meses.map((mes, posicao) => (posicao === indice ? { ...mes, [campo]: valor } : mes)),
    }));
  }

  const totalRealizado = realizado.data ? somarRealizado(realizado.data.meses) : null;

  return (
    <div className="flex flex-col gap-bloco pb-20">
      <ResumoDoAno
        calculado={calculado}
        totalRealizado={totalRealizado}
        ano={funil.ano}
        comCrm={realizado.data?.comCrm ?? true}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <EscalaDeVerba
          valor={entradas.escalaDeVerba}
          aoMudar={(escalaDeVerba) => setEntradas((atual) => ({ ...atual, escalaDeVerba }))}
        />
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-block size-3 rounded bg-secondary" aria-hidden="true" />
          Campos editáveis. Mês vazio repete o anterior (o valor aparece apagado). Passe o mouse num campo preenchido
          para repetir o valor nos meses seguintes.
        </p>
      </div>

      <GradeDoFunil
        entradas={entradas}
        calculado={calculado}
        realizado={realizado.data}
        mesAtual={mesAtual}
        aoMudarMes={mudarMes}
      />

      <div className="flex justify-end">
        {confirmandoExclusao ? (
          <span className="flex flex-wrap items-center gap-2 text-sm">
            Excluir o funil projetado de {funil.ano}? As metas desse ano serão apagadas.
            <Button
              size="sm"
              variant="destructive"
              disabled={remover.isPending}
              onClick={() =>
                remover.mutate(funil.ano, {
                  onSuccess: () => toast.success(`Funil projetado de ${funil.ano} excluído`),
                })
              }
            >
              Excluir
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmandoExclusao(false)}>
              Cancelar
            </Button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmandoExclusao(true)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-destructive hover:underline"
          >
            <Trash2 className="size-3.5" aria-hidden="true" />
            Excluir {funil.ano}
          </button>
        )}
      </div>

      {alterado || salvar.isPending ? (
        <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center px-4">
          <div className="flex items-center gap-3 rounded-full bg-secondary py-2 pr-2 pl-5 text-sm text-secondary-foreground shadow-xl">
            <span>{salvar.isError ? mensagemDoErro(salvar.error) : "Alterações não salvas"}</span>
            <Button
              size="sm"
              variant="ghost"
              className="text-secondary-foreground hover:bg-white/10 hover:text-secondary-foreground"
              onClick={() => setEntradas(entradasDe(funil))}
              disabled={salvar.isPending}
            >
              Descartar
            </Button>
            <Button
              size="sm"
              disabled={salvar.isPending}
              onClick={() =>
                salvar.mutate(
                  { ano: funil.ano, entradas },
                  { onSuccess: () => toast.success(`Funil projetado de ${funil.ano} salvo`) },
                )
              }
            >
              {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Salvar
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EscalaDeVerba({ valor, aoMudar }: { readonly valor: number; readonly aoMudar: (valor: number) => void }) {
  const [texto, setTexto] = useState<string | null>(null);
  return (
    <label className="flex items-center gap-3 text-sm font-medium">
      Escala de verba no ano
      <span className="relative">
        <input
          inputMode="decimal"
          aria-label="Escala de verba no ano, em porcentagem"
          value={texto ?? formatarEntrada("porcentagem", valor)}
          placeholder="0"
          onChange={(evento) => setTexto(evento.target.value)}
          onBlur={() => {
            const lido = lerEntrada("porcentagem", texto ?? "");
            if (texto !== null && lido !== null) aoMudar(lido);
            setTexto(null);
          }}
          className="h-9 w-24 rounded-lg bg-secondary px-3 pr-7 text-right text-sm text-secondary-foreground tabular-nums outline-none focus-visible:ring-2 focus-visible:ring-ice"
        />
        <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-xs text-secondary-foreground/60">
          %
        </span>
      </span>
      <span className="text-xs font-normal text-muted-foreground">aumenta o orçamento de todos os meses</span>
    </label>
  );
}

function ResumoDoAno({
  calculado,
  totalRealizado,
  ano,
  comCrm,
}: {
  readonly calculado: ReturnType<typeof calcularFunil>;
  readonly totalRealizado: ReturnType<typeof somarRealizado> | null;
  readonly ano: number;
  readonly comCrm: boolean;
}) {
  const total = calculado.total;
  const itens = [
    {
      rotulo: `Faturamento ${ano}`,
      meta: formatarValor("moeda", total.faturamentoTotalCentavos),
      atingido: atingido(total.faturamentoTotalCentavos, totalRealizado?.faturamentoCentavos ?? null),
      destaque: true,
    },
    {
      rotulo: "Vendas",
      meta: formatarValor("quantidade", total.vendas),
      atingido: atingido(total.vendas, totalRealizado?.vendas ?? null),
    },
    {
      rotulo: "Orçamento de mídia",
      meta: formatarValor("moeda", total.orcamentoCentavos),
      atingido: atingido(total.orcamentoCentavos, totalRealizado?.investimentoCentavos ?? null),
    },
    { rotulo: "ROAS projetado", meta: formatarValor("multiplicador", total.roas), atingido: null },
  ];

  return (
    <section aria-label={`Resumo das metas de ${ano}`} className="grid gap-bloco sm:grid-cols-2 xl:grid-cols-4">
      {itens.map((item) => (
        <div
          key={item.rotulo}
          className={cn(
            "flex flex-col gap-3 rounded-3xl p-6",
            item.destaque ? "bg-secondary text-secondary-foreground shadow-xl" : "bg-card shadow-sm ring-1 ring-border/70",
          )}
        >
          <p className={cn("text-sm font-medium", item.destaque ? "opacity-75" : "text-muted-foreground")}>{item.rotulo}</p>
          <p className="text-[1.75rem] leading-none font-medium tracking-[-0.03em] tabular-nums">{item.meta}</p>
          <p className={cn("text-xs", item.destaque ? "opacity-75" : "text-muted-foreground")}>
            {item.atingido === null
              ? item.rotulo === "ROAS projetado"
                ? "Faturamento marketing ÷ orçamento"
                : comCrm
                  ? "Sem realizado para comparar"
                  : "Conecte o Kommo para ver o realizado"
              : `${formatarAtingido(item.atingido)} atingido até agora`}
          </p>
        </div>
      ))}
    </section>
  );
}
